import * as React from "react";
import {
  Flex,
  Box,
  Card as RadixCard,
  Button as RadixButton,
  TextField as RadixTextField,
  TextArea as RadixTextArea,
  Checkbox as RadixCheckbox,
  Select,
  Slider as RadixSlider,
  Text as RadixText,
  Separator,
  Tabs as RadixTabs,
  ScrollArea,
  Dialog,
} from "@radix-ui/themes";
import * as PhosphorIcons from "@phosphor-icons/react";
import { v0_9 } from "@a2ui-sdk/react";
import type { StandardCatalog, ChildList, TemplateBinding, DynamicValue } from "@a2ui-sdk/types/0.9";
import { MarkdownTextComponent } from "./MarkdownRenderer";
import { useStringBinding, useDataBinding } from "../hooks/useA2UIStringBinding";
import { formatForInput } from "../utils/dateUtils";

const { useDataModel, useScope, ComponentRenderer, useDispatchAction, useFormBinding } = v0_9;

// --- Helper for rendering children ---
const ChildrenRenderer = ({ surfaceId, children }: { surfaceId: string; children: ChildList }) => {
  if (!children) return null;
  if (Array.isArray(children)) {
    return (
      <>
        {children.map((childId) => (
          <ComponentRenderer key={childId} surfaceId={surfaceId} componentId={childId} />
        ))}
      </>
    );
  }
  const stdRow = v0_9.standardCatalog.components.Row;
  if (stdRow) {
    const StdRow = stdRow as React.FC<any>;
    return <StdRow surfaceId={surfaceId} children={children} />;
  }
  return null;
};

// --- Maps ---
const alignMap: Record<string, "start" | "center" | "end" | "stretch"> = {
  start: "start",
  center: "center",
  end: "end",
  stretch: "stretch",
};

const justifyMap: Record<string, "start" | "center" | "end" | "between"> = {
  start: "start",
  center: "center",
  end: "end",
  spaceBetween: "between",
  spaceAround: "between",
  spaceEvenly: "between",
  stretch: "start",
};

// 1. Text
// Already implemented in MarkdownRenderer

// 2. Row
export const RowComponent: React.FC<StandardCatalog.RowComponentProps & { surfaceId: string; weight?: number }> = ({
  surfaceId,
  children,
  justify = "start",
  align = "stretch",
  weight,
}) => {
  return (
    <Flex
      direction="row"
      gap="3"
      align={alignMap[align]}
      justify={justifyMap[justify]}
      style={weight ? { flexGrow: weight, width: '100%' } : { width: '100%' }}
    >
      <ChildrenRenderer surfaceId={surfaceId} children={children} />
    </Flex>
  );
};

// 3. Column
export const ColumnComponent: React.FC<StandardCatalog.ColumnComponentProps & { surfaceId: string; weight?: number }> = ({
  surfaceId,
  children,
  justify = "start",
  align = "stretch",
  weight,
}) => {
  return (
    <Flex
      direction="column"
      gap="3"
      align={alignMap[align]}
      justify={justifyMap[justify]}
      style={weight ? { flexGrow: weight, width: '100%' } : { width: '100%' }}
    >
      <ChildrenRenderer surfaceId={surfaceId} children={children} />
    </Flex>
  );
};

// 4. List (Vertical or Horizontal)
export const ListComponent: React.FC<StandardCatalog.ListComponentProps & { surfaceId: string; weight?: number }> = ({
  surfaceId,
  children,
  direction = "vertical",
  align = "stretch",
  weight,
}) => {
  return (
    <Flex
      direction={direction === "vertical" ? "column" : "row"}
      gap="2"
      align={alignMap[align]}
      style={weight ? { flexGrow: weight, width: '100%' } : { width: '100%' }}
    >
      <ChildrenRenderer surfaceId={surfaceId} children={children} />
    </Flex>
  );
};

// 5. Divider
export const DividerComponent: React.FC<StandardCatalog.DividerComponentProps & { surfaceId: string; weight?: number }> = ({
  axis = "horizontal",
  weight,
}) => {
  return (
    <Box style={weight ? { flexGrow: weight } : { width: '100%', padding: '8px 0' }}>
      <Separator orientation={axis} size="4" />
    </Box>
  );
};

// 6. Card
export const CardComponent: React.FC<StandardCatalog.CardComponentProps & { surfaceId: string; weight?: number }> = ({
  surfaceId,
  child,
  weight,
}) => {
  return (
    <RadixCard size="2" style={weight ? { flexGrow: weight, width: '100%' } : { width: '100%' }}>
      {child ? <ComponentRenderer surfaceId={surfaceId} componentId={child} /> : null}
    </RadixCard>
  );
};

// 7. Button
export const ButtonComponent: React.FC<StandardCatalog.ButtonComponentProps & { surfaceId: string; weight?: number; componentId: string; disabled?: DynamicValue | boolean; primary?: DynamicValue | boolean }> = ({
  surfaceId,
  componentId,
  child,
  primary = true,
  action,
  weight,
  disabled,
}) => {
  const dispatch = useDispatchAction();
  const dataModel = useDataModel(surfaceId);
  const isDisabled = useDataBinding<boolean>(surfaceId, disabled as any, false);
  const isPrimary = useDataBinding<boolean>(surfaceId, primary as any, typeof primary === 'boolean' ? primary : true);
  
  const handleClick = () => {
    if (action) {
      // action payload v0.9 does not yet match a2ui react sdk payload
      let normalizedAction: Record<string, any> = action as Record<string, any>;
      if (normalizedAction.event) {
        normalizedAction = normalizedAction.event;
      } else if (normalizedAction.functionCall) {
        normalizedAction = { 
          name: normalizedAction.functionCall.call, 
          context: normalizedAction.functionCall.args 
        };
      } else if (normalizedAction.call) {
        normalizedAction = {
          name: normalizedAction.call,
          context: normalizedAction.args
        };
      }
      dispatch(surfaceId, componentId, normalizedAction as any);
    }
  };

  return (
    <RadixButton
      variant={isPrimary ? "solid" : "soft"}
      onClick={handleClick}
      style={weight ? { flexGrow: weight } : undefined}
      disabled={isDisabled}
    >
      {child ? <ComponentRenderer surfaceId={surfaceId} componentId={child} /> : null}
    </RadixButton>
  );
};

// 8. Icon
const iconMap: Record<string, keyof typeof PhosphorIcons> = {
  accountCircle: "UserCircle",
  add: "Plus",
  arrowBack: "ArrowLeft",
  arrowForward: "ArrowRight",
  attachFile: "Paperclip",
  calendarToday: "Calendar",
  call: "Phone",
  camera: "Camera",
  check: "Check",
  close: "X",
  delete: "Trash",
  download: "Download",
  edit: "Pencil",
  event: "CalendarBlank",
  error: "WarningCircle",
  favorite: "Heart",
  favoriteOff: "HeartBreak",
  folder: "Folder",
  help: "Question",
  home: "House",
  info: "Info",
  locationOn: "MapPin",
  lock: "LockKey",
  lockOpen: "LockOpen",
  mail: "Envelope",
  menu: "List",
  moreVert: "DotsThreeVertical",
  moreHoriz: "DotsThree",
  notificationsOff: "BellSlash",
  notifications: "Bell",
  payment: "CreditCard",
  person: "User",
  phone: "Phone",
  photo: "Image",
  print: "Printer",
  refresh: "ArrowsClockwise",
  search: "MagnifyingGlass",
  send: "PaperPlaneRight",
  settings: "Gear",
  share: "ShareNetwork",
  shoppingCart: "ShoppingCart",
  star: "Star",
  starHalf: "StarHalf",
  starOff: "Star",
  upload: "Upload",
  visibility: "Eye",
  visibilityOff: "EyeSlash",
  warning: "Warning",
};

export const IconComponent: React.FC<StandardCatalog.IconComponentProps & { surfaceId: string; weight?: number }> = ({
  surfaceId,
  name,
  weight,
}) => {
  const resolvedName = useStringBinding(surfaceId, name, "");
  if (!resolvedName) return null;
  
  const IconRef = PhosphorIcons[iconMap[resolvedName] || "Question"] as React.ElementType;
  
  // FIX: wrap in Flex display="flex" align="center" justify="center" to fix the inline text issue.
  return (
    <Flex display="flex" align="center" justify="center" style={{ flexShrink: 0, ...(weight ? { flexGrow: weight } : {}) }}>
      {IconRef ? <IconRef size={20} /> : null}
    </Flex>
  );
};

// 9. TextField
export const TextFieldComponent: React.FC<StandardCatalog.TextFieldComponentProps & { surfaceId: string; weight?: number; disabled?: DynamicValue | boolean }> = ({
  surfaceId,
  label,
  value,
  variant = "shortText",
  weight,
  disabled,
}) => {
  const resolvedLabel = useStringBinding(surfaceId, label, "");
  const [val, setVal] = useFormBinding(surfaceId, value, "");
  const isDisabled = useDataBinding<boolean>(surfaceId, disabled as any, false);

  return (
    <Box style={weight ? { flexGrow: weight } : { width: '100%' }}>
      {resolvedLabel && (
        <RadixText as="div" size="2" mb="1" weight="bold">
          {resolvedLabel}
        </RadixText>
      )}
      {variant === "longText" ? (
        <RadixTextArea
          value={val as any}
          onChange={(e) => setVal(e.target.value)}
          placeholder={resolvedLabel}
          resize={'vertical'}
          rows={5}
          disabled={isDisabled}
        />
      ) : (
        <RadixTextField.Root
          type={variant === "obscured" ? "password" : variant === "number" ? "number" : "text"}
          value={val as any}
          onChange={(e) => setVal(e.target.value)}
          placeholder={resolvedLabel}
          disabled={isDisabled}
        />
      )}
    </Box>
  );
};

// 10. CheckBox
export const CheckBoxComponent: React.FC<StandardCatalog.CheckBoxComponentProps & { surfaceId: string; weight?: number }> = ({
  surfaceId,
  label,
  value,
  weight,
}) => {
  const resolvedLabel = useStringBinding(surfaceId, label, "");
  const [val, setVal] = useFormBinding(surfaceId, value, false);

  return (
    <RadixText as="label" size="2" style={{ display: 'flex', alignItems: 'center', gap: '8px', ...(weight ? { flexGrow: weight } : {}) }}>
      <RadixCheckbox checked={val} onCheckedChange={(c) => setVal(c === true)} />
      {resolvedLabel}
    </RadixText>
  );
};

// 11. Slider
export const SliderComponent: React.FC<StandardCatalog.SliderComponentProps & { surfaceId: string; weight?: number }> = ({
  surfaceId,
  label,
  min,
  max,
  value,
  weight,
}) => {
  const resolvedLabel = useStringBinding(surfaceId, label, "");
  const [val, setVal] = useFormBinding(surfaceId, value, min);

  return (
    <Box style={weight ? { flexGrow: weight } : { width: '100%' }}>
      {resolvedLabel && (
        <RadixText as="div" size="2" mb="1" weight="bold">
          {resolvedLabel}: {val}
        </RadixText>
      )}
      <RadixSlider value={[val]} min={min} max={max} onValueChange={(v) => setVal(v[0])} />
    </Box>
  );
};

// 12. ChoicePicker
export const ChoicePickerComponent: React.FC<StandardCatalog.ChoicePickerComponentProps & { surfaceId: string; weight?: number }> = ({
  surfaceId,
  label,
  options,
  value,
  variant = "mutuallyExclusive",
  weight,
}) => {
  const resolvedLabel = useStringBinding(surfaceId, label, "");
  const [val, setVal] = useFormBinding(surfaceId, value, [] as string[]);

  // Simplified: treat all as a Radix Select for single, or fallback for multiple
  const isMultiple = variant === "multipleSelection";
  const selectedValue = Array.isArray(val) && val.length > 0 ? val[0] : "";

  return (
    <Box style={weight ? { flexGrow: weight } : { width: '100%' }}>
      {resolvedLabel && (
        <RadixText as="div" size="2" mb="1" weight="bold">
          {resolvedLabel}
        </RadixText>
      )}
      <Select.Root
        value={selectedValue}
        onValueChange={(v) => setVal(isMultiple ? [v] : [v])}
      >
        <Select.Trigger placeholder="Select an option" style={{ width: '100%' }} />
        <Select.Content>
          {options.map((opt, i) => {
             const optLabel = typeof opt.label === 'string' ? opt.label : opt.value;
             return (
               <Select.Item key={i} value={opt.value}>
                 {optLabel}
               </Select.Item>
             );
          })}
        </Select.Content>
      </Select.Root>
    </Box>
  );
};

// 13. Image
export const ImageComponent: React.FC<StandardCatalog.ImageComponentProps & { surfaceId: string; weight?: number }> = ({
  surfaceId,
  url,
  fit = "cover",
  variant = "mediumFeature",
  weight,
}) => {
  const resolvedUrl = useStringBinding(surfaceId, url, "");
  
  if (!resolvedUrl) return null;
  
  let size = '100%';
  if (variant === 'icon') size = '32px';
  else if (variant === 'avatar') size = '64px';
  
  return (
    <Box style={weight ? { flexGrow: weight } : {}}>
      <img src={resolvedUrl} style={{ width: size, height: size, objectFit: fit, borderRadius: variant === 'avatar' ? '50%' : 'var(--radius-2)' }} alt="A2UI Image" />
    </Box>
  );
};

// 14. Video
export const VideoComponent: React.FC<StandardCatalog.VideoComponentProps & { surfaceId: string; weight?: number }> = ({
  surfaceId,
  url,
  weight,
}) => {
  const resolvedUrl = useStringBinding(surfaceId, url, "");
  if (!resolvedUrl) return null;

  return (
    <Box style={weight ? { flexGrow: weight } : { width: '100%' }}>
      <video src={resolvedUrl} controls style={{ width: '100%', borderRadius: 'var(--radius-2)' }} />
    </Box>
  );
};

// 15. AudioPlayer
export const AudioPlayerComponent: React.FC<StandardCatalog.AudioPlayerComponentProps & { surfaceId: string; weight?: number }> = ({
  surfaceId,
  url,
  description,
  weight,
}) => {
  const resolvedUrl = useStringBinding(surfaceId, url, "");
  const resolvedDesc = useStringBinding(surfaceId, description, "");
  if (!resolvedUrl) return null;

  return (
    <Flex direction="column" gap="2" style={weight ? { flexGrow: weight } : { width: '100%' }}>
      {resolvedDesc && <RadixText size="2">{resolvedDesc}</RadixText>}
      <audio src={resolvedUrl} controls style={{ width: '100%' }} />
    </Flex>
  );
};

// 16. Tabs
const TabTrigger = ({ surfaceId, tab }: { surfaceId: string, tab: StandardCatalog.TabItem }) => {
  const title = useStringBinding(surfaceId, tab.title, "");
  return <RadixTabs.Trigger value={tab.child}>{title || tab.child}</RadixTabs.Trigger>;
};

export const TabsComponent: React.FC<StandardCatalog.TabsComponentProps & { surfaceId: string; weight?: number }> = ({
  surfaceId,
  tabs,
  weight,
}) => {
  if (!tabs || tabs.length === 0) return null;

  return (
    <Box style={weight ? { flexGrow: weight, minWidth: 0, width: '100%' } : { width: '100%', minWidth: 0 }}>
      <RadixTabs.Root defaultValue={tabs[0].child} style={{ width: '100%', minWidth: 0 }}>
        {/* Use ScrollArea for the tab strip so overflowing tabs are scrollable with a styled scrollbar */}
        <ScrollArea scrollbars="horizontal" style={{ width: '100%' }}>
          <RadixTabs.List style={{ whiteSpace: 'nowrap', width: 'max-content', minWidth: '100%' }}>
            {tabs.map((tab) => (
              <TabTrigger key={tab.child} surfaceId={surfaceId} tab={tab} />
            ))}
          </RadixTabs.List>
        </ScrollArea>
        <Box pt="3">
          {tabs.map((tab) => (
            <RadixTabs.Content key={tab.child} value={tab.child}>
              <ComponentRenderer surfaceId={surfaceId} componentId={tab.child} />
            </RadixTabs.Content>
          ))}
        </Box>
      </RadixTabs.Root>
    </Box>
  );
};

// 17. Modal
export const ModalComponent: React.FC<StandardCatalog.ModalComponentProps & { surfaceId: string; weight?: number }> = ({
  surfaceId,
  trigger,
  content,
  weight,
}) => {
  return (
    <Dialog.Root>
      <Dialog.Trigger>
        <Box style={{ cursor: 'pointer', display: 'inline-block', ...(weight ? { flexGrow: weight } : {}) }}>
          <ComponentRenderer surfaceId={surfaceId} componentId={trigger} />
        </Box>
      </Dialog.Trigger>
      <Dialog.Content maxWidth="450px">
        <ComponentRenderer surfaceId={surfaceId} componentId={content} />
        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <RadixButton variant="soft" color="gray">
              Close
            </RadixButton>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

// 18. DateTimeInput
export const DateTimeInputComponent: React.FC<StandardCatalog.DateTimeInputComponentProps & { surfaceId: string; weight?: number }> = ({
  surfaceId,
  label,
  value,
  enableDate = true,
  enableTime = false,
  weight,
}) => {
  const resolvedLabel = useStringBinding(surfaceId, label, "");
  const [val, setVal] = useFormBinding(surfaceId, value, "");

  const type = enableDate && enableTime ? "datetime-local" : enableTime ? "time" : "date";
  const displayValue = formatForInput(val ? String(val) : "", type);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (!newValue) {
      setVal("");
      return;
    }
    
    try {
      if (type === "datetime-local") {
        const d = new Date(newValue);
        if (!isNaN(d.getTime())) {
          setVal(d.toISOString());
          return;
        }
      }
    } catch (err) {
      // Fallback
    }
    setVal(newValue);
  };

  return (
    <Box style={weight ? { flexGrow: weight } : { width: '100%' }}>
      {resolvedLabel && (
        <RadixText as="div" size="2" mb="1" weight="bold">
          {resolvedLabel}
        </RadixText>
      )}
      <RadixTextField.Root
        type={type}
        value={displayValue}
        onChange={handleChange}
      />
    </Box>
  );
};

export const customCatalogComponents = {
  Text: MarkdownTextComponent,
  Row: RowComponent,
  Column: ColumnComponent,
  List: ListComponent,
  Divider: DividerComponent,
  Card: CardComponent,
  Button: ButtonComponent,
  Icon: IconComponent,
  TextField: TextFieldComponent,
  CheckBox: CheckBoxComponent,
  Slider: SliderComponent,
  ChoicePicker: ChoicePickerComponent,
  Image: ImageComponent,
  Video: VideoComponent,
  AudioPlayer: AudioPlayerComponent,
  Tabs: TabsComponent,
  Modal: ModalComponent,
  DateTimeInput: DateTimeInputComponent,
};
